import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/backend`;

export default function Chatbot() {
    const { t } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setMessages([{ role: "bot", text: t.chatbot.welcome }]);
        }
    }, [isOpen, messages.length, t.chatbot.welcome]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || loading) return;

        const userMsg = input.trim();
        setInput("");
        setMessages((prev) => [...prev, { role: "user", text: userMsg }]);
        setLoading(true);

        try {
            const res = await axios.post(`${API}/ch_1f7a2c.php`, {
                message: userMsg,
                session_id: sessionId,
            });
            setSessionId(res.data.session_id);
            setMessages((prev) => [...prev, { role: "bot", text: res.data.response }]);
        } catch {
            const errorMsg = t.chatbot.welcome.includes("Hola")
                ? "Lo siento, no pude procesar tu mensaje. Intentalo de nuevo."
                : "Sorry, I couldn't process your message. Please try again.";
            setMessages((prev) => [...prev, { role: "bot", text: errorMsg }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {isOpen && (
                <div
                    className="fixed bottom-20 right-4 md:right-8 w-[90vw] md:w-[400px] h-[500px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden"
                    data-testid="chatbot-panel"
                >
                    <div className="bg-[#0A1628] text-white p-4 flex items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <MessageCircle className="w-5 h-5 text-blue-400" />
                            <span className="font-semibold text-sm">{t.chatbot.title}</span>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                            data-testid="chatbot-close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {messages.map((msg, i) => (
                            <div
                                key={i}
                                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} chat-message`}
                            >
                                <div
                                    className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm leading-relaxed ${
                                        msg.role === "user"
                                            ? "bg-blue-600 text-white"
                                            : "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                                    }`}
                                >
                                    {msg.text}
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="flex justify-start chat-message">
                                <div className="bg-slate-100 dark:bg-slate-800 rounded-xl px-4 py-2.5 text-sm">
                                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="p-3 border-t border-slate-200 dark:border-slate-800 flex-shrink-0">
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSend();
                            }}
                            className="flex gap-2"
                        >
                            <Input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={t.chatbot.placeholder}
                                disabled={loading}
                                className="h-10 text-sm"
                                data-testid="chatbot-input"
                            />
                            <Button
                                type="submit"
                                disabled={loading || !input.trim()}
                                size="icon"
                                className="bg-blue-600 hover:bg-blue-700 h-10 w-10 flex-shrink-0"
                                data-testid="chatbot-send"
                            >
                                <Send className="w-4 h-4" />
                            </Button>
                        </form>
                    </div>
                </div>
            )}

            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed bottom-4 right-4 md:right-8 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-xl z-50"
                data-testid="chatbot-trigger"
            >
                {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
            </button>
        </>
    );
}
